import React from "react";
import get from "lodash/get";
import Icon from "assets/icons";
import { AnyObject } from "global";
import FormGroup from "../formGroup";
import FormLabel from "../formLabel";
import ErrorField from "../errorField";
import { isEmpty } from "shared/utils";
import { TagInputConfig } from "./types";
import FormTextField from "../textField";
import { TagFieldConfig } from "../types";
import KeyCode from "shared/enums/KeyCode";
import Button from "shared/components/button";
import Validation from "shared/form/Validation";
import { useCopies } from "shared/hooks/useCopies";
import { alphaNumeric } from "shared/utils/regex";
import FormOverwriteField from "../overwriteField";
import { useField, useFormikContext } from "formik";
import { FieldResetWhenRemoved } from "shared/form/util";
import { MAX_TAG_LENGTH, MIN_TAG_LENGTH, TAG_INPUT_FIELD_NAME } from "shared/utils/constants";
import classNames from "classnames";
import { ConflictTagType, ConflictType } from "shared/form/renderer/enums/Conflict";
import FieldValueCount from "shared/components/fieldValueCount";
import sortBy from "lodash/sortBy";
import { ConflictTag } from "../overwriteField/components/conflictTag";

/**
 * @name TagField
 * @todo add css and remove inline css
 */
function TagField(props: TagFieldConfig) {
  const { setFieldTouched, setFieldValue, validateForm, validateOnChange, initialValues } =
    useFormikContext<AnyObject>();
  const [{ value }, meta] = useField({
    name: props.name,
    validate: props.validate
  });
  const copies = useCopies();

  const isMaximumTagLimitReached = React.useMemo(() => (value || []).length === props.maximum, [props.maximum, value]);

  const onTagCreate = React.useCallback(
    async tag => {
      const updatedValue = [...(value || []), tag];
      await setFieldValue(props.name, updatedValue, false);
      if (!meta.touched) {
        await setFieldTouched(props.name, true, false);
      }
      validateOnChange && (await validateForm());
      return true;
    },
    [meta.touched, props.name, setFieldTouched, setFieldValue, validateForm, validateOnChange, value]
  );

  const onTagRemove = React.useCallback(
    tag => {
      setFieldValue(
        props.name,
        value?.filter((item: string) => item !== tag)
      );
    },
    [props.name, setFieldValue, value]
  );

  const addedTagList = React.useMemo(() => sortBy(value, o => o.toLowerCase()), [value]);

  const lowerCasedConflictValues = React.useMemo(
    () => (props.conflictValue ? props.conflictValue.map((o: string) => o.toLowerCase()) : []),
    [props.conflictValue]
  );

  const lowerCasedInitialValues = React.useMemo(
    () => (initialValues[props.name] ? initialValues[props.name].map((o: string) => o.toLowerCase()) : []),
    [initialValues, props.name]
  );

  const lowerCasedValues = React.useMemo(() => (value || []).map((o: string) => o.toLowerCase()), [value]);

  const removedTags = React.useMemo(() => {
    const toReturn: Array<string> = [];
    if (!props.conflictValue?.length) {
      return toReturn;
    }
    props.conflictValue.forEach((tag: string) => {
      if (!lowerCasedValues.includes(tag.toLowerCase()) && !lowerCasedInitialValues.includes(tag.toLowerCase())) {
        toReturn.push(tag);
      }
    });
    return toReturn;
  }, [lowerCasedInitialValues, lowerCasedValues, props.conflictValue]);

  const getConflictTagToDisplay = React.useCallback(
    (tag: string) => {
      if (!lowerCasedConflictValues.length) {
        return undefined;
      }
      const tagLowerCased = tag.toLowerCase();
      if (lowerCasedInitialValues.includes(tagLowerCased) && !lowerCasedConflictValues.includes(tagLowerCased)) {
        return ConflictTagType.NEW_VALUE;
      }
      return undefined;
    },
    [lowerCasedConflictValues, lowerCasedInitialValues]
  );

  return (
    <FormGroup className={props.className} fullWidth={props.fullWidth} indented={props.indented}>
      <FormLabel
        label={props.label}
        required={props.required}
        additionalLabel={props.additionalLabel}
        infoLabel={props.infoLabel}
      />
      <div className="tagField">
        {!props.disabled && (
          <TagInput
            name={`${props.name}-${TAG_INPUT_FIELD_NAME}`}
            disabled={props.disabled || isMaximumTagLimitReached}
            onTagCreate={onTagCreate}
            addedTags={(value || []) as Array<string>}
          />
        )}
        <div
          className={classNames("tagField--tagsBlock", {
            disabled: props.disabled
          })}>
          {addedTagList.length > 0 || (removedTags?.length || 0) > 0 ? (
            <React.Fragment>
              {addedTagList
                .filter(o => !getConflictTagToDisplay(o))
                .map((tag: string, idx: number) => (
                  <DisplayTagWithRemoveIcon
                    key={idx}
                    tag={tag}
                    disabled={props.disabled}
                    onTagRemove={onTagRemove}
                    conflictTagType={getConflictTagToDisplay(tag)}
                  />
                ))}
              {addedTagList
                .filter(o => getConflictTagToDisplay(o) === ConflictTagType.NEW_VALUE)
                .map((tag: string, idx: number) => (
                  <DisplayTagWithRemoveIcon
                    key={idx}
                    tag={tag}
                    disabled={props.disabled}
                    onTagRemove={onTagRemove}
                    conflictTagType={getConflictTagToDisplay(tag)}
                  />
                ))}
              {removedTags?.map((tag: string, idx: number) => (
                <div className="tagField--tagsBlock_tag" key={idx}>
                  <span>
                    {tag} <ConflictTag conflictTagType={ConflictTagType.DELETED_VALUE} />
                  </span>
                </div>
              ))}
            </React.Fragment>
          ) : (
            <span className="tagField--notAdded">{copies.get("no.tags.added")}</span>
          )}
        </div>
        <FieldValueCount className="tagField--count" maxCount={props.maximum} value={value?.length} />
        <ErrorField meta={meta} />
        {!isEmpty(props.conflictValue) && (
          <FormOverwriteField forFieldName={props.name} disabled={props.disabled} conflictType={ConflictType.TAG} />
        )}
        {props.enableFieldReset && <FieldResetWhenRemoved name={props.name} />}
      </div>
    </FormGroup>
  );
}

export default TagField;

function TagInput(props: TagInputConfig) {
  const copies = useCopies();
  const { errors, setFieldValue } = useFormikContext<AnyObject>();

  const hasError = React.useMemo(() => Boolean(get(errors, props.name)), [errors, props.name]);

  const createTagHandler = React.useCallback(
    async tag => {
      if (tag && !hasError) {
        await setFieldValue(props.name, "");
        await props.onTagCreate(tag);
      }
    },
    [hasError, props, setFieldValue]
  );

  const keyDownHandler = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.code === KeyCode.ENTER) {
        event.preventDefault();
        createTagHandler(event.currentTarget.value.trim());
      }
    },
    [createTagHandler]
  );

  const tagValidation = React.useCallback(
    (e: any) =>
      Validation.textFieldValidation({
        copies,
        required: false,
        min: MIN_TAG_LENGTH,
        max: MAX_TAG_LENGTH,
        regex: alphaNumeric,
        regexMsg: copies.get("alphanumeric.allowed"),
        minCharactersCopyKey: "minimum.characters.without.spaces",
        maxCharactersCopyKey: "maximum.characters.without.spaces"
      })
        .test("duplicate tag ", copies.get("duplicate.tag.not.allowed"), function (value) {
          if (isEmpty(value)) return true;
          return !!value && !props.addedTags.some(tag => tag.toLowerCase() === value?.trim()?.toLowerCase());
        })
        .validate(e)
        .then(() => undefined)
        .catch(({ errors }: any) => errors[0]),
    [copies, props.addedTags]
  );

  return (
    <div className="tagField--input">
      <FormTextField
        label={""}
        name={props.name}
        fullWidth={false}
        disabled={props.disabled}
        validate={tagValidation}
        onKeyDown={keyDownHandler}
      />
      <span className="tagField--input_note">{copies.get("tag.field.enter.to.save.msg")}</span>
    </div>
  );
}

function DisplayTagWithRemoveIcon(props: {
  tag: string;
  conflictTagType?: ConflictTagType;
  disabled?: boolean;
  onTagRemove: (tag: string) => void;
}) {
  return (
    <div className="tagField--tagsBlock_tag">
      <span>
        {props.tag} <ConflictTag conflictTagType={props.conflictTagType} />
      </span>
      {!props.disabled && <Button icon={<Icon.Close />} onClick={() => props.onTagRemove(props.tag)} />}
    </div>
  );
}
